import { Form, Formik } from 'formik';
import React, { FC, ReactElement, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { COMMAND_STATE, ENROLL_TYPE, MONITORING_COMMAND, MONITORING_COMMAND_STATE } from '../../../constants/command-status';
import { useLocale } from '../../../contexts/localeContext';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { useIcaWebsocketContext } from '../../../contexts/websocket/IcaWebsocketContext';
import { EnrolmentDevice } from '../../../models/EnrolmentDevices';
import { EnrolmentTask } from '../../../models/EnrolmentTask';
import { ExecuteCommand } from '../../../models/ExecuteCommand';
import { ListQuery } from '../../../models/ListQuery';
import { MultiSelectOption } from '../../../models/MultiSelectOption';
import { Name } from '../../../models/Name';
import { Tag } from '../../../models/Tag';
import WizardSideBarStep from '../../../models/WizardSideBarStep';
import { getBiometricNamesWithPagination } from '../../../services/biometricService';
import { saveCredential } from '../../../services/credentialsService';
import { enrolHand } from '../../../services/executeCommandService';
import { getErrorMessage } from '../../../services/utilities/ServiceError';
import { IShowEnrollValidOptions } from '../../../services/wsPluginService/plugins/base/pluginModels';
import { buildCardData, generateNewRandomPAC } from '../../../utilities/CredentialUtil';
import { appendFilter } from '../../../utilities/filters';
import FullScreenActivityFrame from '../../activity-frame/full-screen-activity-spinner';
import { useDialog } from '../../dialog/DialogContext';
import Wizard from '../../wizard/wizard';
import ChooseCard from '../choose-card';
import DetailsForm from '../details-form';
import SelectDeviceForm from '../select-device-form';
import SubTasksForm from '../sub-tasks-form';
import SelectHand from './select-hand';
import SummaryForm from './summary-form';

interface Props {
    availablePlugins: IShowEnrollValidOptions;
    availableSystemDeviceIds: number[];
    cardInfo: Tag;
    onClose: () => void;
    defaultContent: ReactElement[];
    defaultSteps: WizardSideBarStep[];
    onBack: (event: any) => void;
    updateBadge: (badge: number) => void;
    masterId: number | undefined;
}

const EnrolHandWizard: FC<Props> = ({ availablePlugins, cardInfo, onClose, defaultContent, defaultSteps, onBack, updateBadge, availableSystemDeviceIds, masterId }) => {
    const { translate } = useLocale();
    const [step, setStep] = useState<number>(1);
    const [deviceType, setDeviceType] = useState('');
    const [listParameters, setListParameters] = useState<ListQuery>();
    const [selectedHand, setSelectedHand] = useState<MultiSelectOption>({ value: "", label: "" });
    const [device, setDevice] = useState<Name>();
    const [busyScanning, setBusyScanning] = useState<boolean>(false);
    const enrolmentDevice = new EnrolmentDevice(translate);
    const enrolmentTask = new EnrolmentTask(translate);
    const icaWebSocket = useIcaWebsocketContext();
    const dialog = useDialog();
    const [newCard, setNewCard] = useState<Tag>(cardInfo);
    const [isNewCard, setIsNewCard] = useState<boolean>(false);
    const [selectedTag, setSelectedTag] = useState<MultiSelectOption>({ value: "", label: "" });

    const onMessage = (response: any) => {
        const jsonData = JSON.parse(response.data);
        if (jsonData.command === MONITORING_COMMAND.MONITORING_ENROL && jsonData.attributes.subCommand === ENROLL_TYPE.ENROLL_IDEMIA_FINGER) {
            if (jsonData.attributes.cmdState === COMMAND_STATE.COMMAND_COMPLETE) {
                toast.success(translate.byKey(TranslationKeys.credentialSaved));
                updateBadge(4);
            }
            else {
                toast.warn(jsonData.attributes.errMessage);
            }
            onClose();
            setBusyScanning(false);
        }
    }

    useEffect(() => {
        if (icaWebSocket.connectionState) {
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_ENROL, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_START);
            icaWebSocket.setOnMessage(onMessage)
        }

        return () => {
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_ENROL, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);
        }
    }, [icaWebSocket.connectionState]);

    const onLocalNext = (event) => { 
        if (event.code) {
            setSelectedTag({ ...selectedTag, label: event.code });
        }

        if (event.task) {
            setDeviceType(event.description);
            const allHandDevices = [...enrolmentDevice.ENROL_HAND_PLUGIN, ...enrolmentDevice.ENROL_HAND_DEVICES];
            const selectedDeviceId = allHandDevices.find(item => item.label === event.description)?.id.toString();
            setListParameters({ ...listParameters, filters: appendFilter(selectedDeviceId, 'Device Type', 'bioType.id', []) });
        }

        setStep(step + 1);
    }

    const onLocalBack = (event: any) => {
        if (step === 1) {
            onBack(event);
            return;
        }

        if (step === 2) setDeviceType("");
        setStep(step - 1);
    }

    const onLocalSave = async (values) => {
        try {
            setBusyScanning(true)

            //Create new tag if needed
            let newTag: any;
            if (isNewCard) {
                newTag = await saveCredential({ ...buildCardData({ ...values, masterId: masterId }, translate) });
            }
            const command: ExecuteCommand = {
                cmd: ENROLL_TYPE.ENROLL,
                cmdSubType: ENROLL_TYPE.ENROLL_IDEMIA_HAND,
                tagId: newTag ? newTag.content.data.id : selectedTag.value,
                captureType: selectedHand.value,
                deviceId: device?.value
            };
            await enrolHand(command);

        } catch (error) {
            const err = error as any;

            if (err.status === 408) {
                console.warn("Stopping scan due to timeout");
            }

            dialog.pushError({
                title: translate.byKey(TranslationKeys.error),
                message: getErrorMessage(err)
            });

            setBusyScanning(false);
        }
    }

    const onChooseCardNext = (e) => {
        if (e.value && e.value === enrolmentDevice.MANUAL_CODE_TYPE.value) {
            setNewCard({
                ...e.values,
                code: cardInfo.code,
                typeId: cardInfo.typeId,
                startDate: undefined,
                startTime: '',
                expiryDate: undefined,
                expiryTime: '',
            });
            setIsNewCard(true);
        } else if (e.value && e.value === enrolmentDevice.RANDOM_PAC.value) {
            const tag: Tag = generateNewRandomPAC(newCard, translate)
            setNewCard({
                ...e.values,
                code: tag.code,
                tagDisplay: tag.code,
                type: tag.type,
                typeId: tag.typeId,
                startDate: undefined,
                startTime: '',
                expiryDate: undefined,
                expiryTime: '',
            });
            setSelectedTag({ ...selectedTag, label: tag.code })
            setIsNewCard(true);
        } else {
            setSelectedTag({ value: e.value, label: e.code })
            setStep(step + 1);
        }
    }

    const steps: WizardSideBarStep[] = [
        ...defaultSteps,
        {
            title: translate.byKey(TranslationKeys.taskProcedure),
            description: deviceType,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.device),
            description: device?.label ?? '',
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.enrolType),
            description: selectedHand.label,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.card),
            description: selectedTag.label,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.summary),
            description: '',
            substeps: []
        }
    ];

    const content = [
        ...defaultContent,
        <SubTasksForm
            enrollMethod={enrolmentTask.ENROL_HAND.value}
            onNext={(event) => onLocalNext(event)}
            pluginsAvailable={availablePlugins}
            onBack={(event) => onLocalBack(event)}
            availableSystemDeviceIds={availableSystemDeviceIds} />,
        <SelectDeviceForm
            dataMethod={getBiometricNamesWithPagination}
            onNext={(event) => onLocalNext(event)}
            onBack={(event) => onLocalBack(event)}
            setSelectedDevice={setDevice}
            listParameters={listParameters} />,
        <SelectHand
            onNext={(event) => onLocalNext(event)}
            onBack={(event) => onLocalBack(event)}
            setSelectedHand={setSelectedHand} />,
        isNewCard ?
            <DetailsForm
                onBack={(e) => { setIsNewCard(false); }}
                onNext={onLocalNext}
                cardInfo={newCard} /> :
            <ChooseCard
                masterId={masterId}
                onBack={onLocalBack}
                onNext={onChooseCardNext} />,
        <SummaryForm
            onBack={(event) => onLocalBack(event)}
            taskDescription={enrolmentTask.ENROL_HAND.label}
            device={device?.label ?? ''}
            deviceType={deviceType}
            cardCode={selectedTag.label}
            enrolmentType={selectedHand.label} />
    ]

    return <>
        {busyScanning ? <FullScreenActivityFrame message={translate.byKey(TranslationKeys.scanning)} /> : null}
        <Formik
            enableReinitialize={true}
            initialValues={cardInfo}
            onSubmit={onLocalSave}>
            <Form>
                <Wizard name={translate.byKey(TranslationKeys.enrollCredential)}
                    step={step}
                    onClose={() => onClose()}
                    sidebarSteps={steps}>
                    {content}
                </Wizard>
            </Form>
        </Formik>
    </>
}

export default EnrolHandWizard;
